import type {
  AnyModelConfig,
  AssignableModelFields,
  GetConfigFields,
  GetModelConfig,
  Model,
  ModelConstructor,
} from "@mixitone/oom";

type CopierField<C extends AnyModelConfig> = keyof GetConfigFields<C> & string;

type MissingCopierFields<
  Included extends readonly string[],
  Excluded extends readonly string[],
  All extends string,
> = Exclude<All, Included[number] | Excluded[number]>;

type DuplicateCopierFields<Included extends readonly string[], Excluded extends readonly string[]> = Extract<
  Included[number],
  Excluded[number]
>;

type AssertNever<T, Message extends string> = [T] extends [never] ? unknown : Message;

/**
 * included + excluded must be a partition of every model field (except `id`).
 * A missing field becomes a type error so `pnpm run tsc` / lint fails instead of
 * crashing screens when the copier is constructed.
 */
export type ModelCopierAttributes<
  Included extends readonly string[],
  Excluded extends readonly string[],
  All extends string,
> = {
  included: Included;
  excluded: Excluded;
} & AssertNever<
  MissingCopierFields<Included, Excluded, All>,
  `Missing ModelCopier fields: ${MissingCopierFields<Included, Excluded, All>}`
> &
  AssertNever<
    DuplicateCopierFields<Included, Excluded>,
    `Fields in both included and excluded: ${DuplicateCopierFields<Included, Excluded>}`
  >;

/**
 * Copies selected fields onto a new model instance.
 * Prefer {@link modelCopier} so field lists are checked at compile time.
 */
export class ModelCopier<M extends Model<any>, C extends GetModelConfig<M> = GetModelConfig<M>> {
  private readonly includedAttributes: Array<CopierField<C>>;

  constructor(
    private readonly ModelClass: ModelConstructor<M>,
    private readonly modelName: string,
    attributes: {
      included: ReadonlyArray<CopierField<C>>;
      excluded: ReadonlyArray<CopierField<C>>;
    },
  ) {
    this.includedAttributes = [...attributes.included];
    this.validateAttributeLists(attributes);
  }

  /**
   * Runtime check that included + excluded cover all model fields.
   * `id` is not a config field and must not appear in the lists.
   */
  private validateAttributeLists(attributes: {
    included: ReadonlyArray<CopierField<C>>;
    excluded: ReadonlyArray<CopierField<C>>;
  }): void {
    const allFields = new Set(Object.keys(this.ModelClass.config.fields));
    const includedSet = new Set(attributes.included);
    const excludedSet = new Set(attributes.excluded);
    const combined = new Set([...includedSet, ...excludedSet]);

    const duplicates = [...includedSet].filter((field) => excludedSet.has(field));
    if (duplicates.length > 0) {
      throw new Error(
        `${this.modelName}: Fields appear in both included and excluded lists: ${duplicates.join(", ")}`,
      );
    }

    const missing = [...allFields].filter((field) => !combined.has(field));
    const extra = [...combined].filter((field) => !allFields.has(field));

    if (missing.length > 0 || extra.length > 0) {
      const errors: string[] = [];
      if (missing.length > 0) {
        errors.push(`Missing fields: ${missing.join(", ")}`);
      }
      if (extra.length > 0) {
        errors.push(`Extra fields: ${extra.join(", ")}`);
      }
      throw new Error(`${this.modelName}: Attribute lists don't match model fields. ${errors.join(". ")}`);
    }
  }

  copy(source: M, overrides: AssignableModelFields<C> = {}): M {
    const attributes: AssignableModelFields<C> = {};
    for (const field of this.includedAttributes) {
      attributes[field] = source.readAttribute(field);
    }
    Object.assign(attributes, overrides as AssignableModelFields<C>);
    return new this.ModelClass(attributes);
  }
}

/** Build a copier whose included/excluded lists must name every model field. */
export function modelCopier<
  M extends Model<any>,
  const Included extends readonly CopierField<GetModelConfig<M>>[],
  const Excluded extends readonly CopierField<GetModelConfig<M>>[],
>(
  ModelClass: ModelConstructor<M>,
  modelName: string,
  attributes: ModelCopierAttributes<Included, Excluded, CopierField<GetModelConfig<M>>>,
): ModelCopier<M> {
  return new ModelCopier(ModelClass, modelName, attributes);
}
